// =============================================
// entityLinkingService.ts — entities UPSERT + node linking (pure)
// =============================================
// Ekstrakt z EntitiesSection.handleExtractEntities + linkEntitiesToNodes
// (STOP-C planu auto-generate-maps).
//
// CEL: dac bulk runnerowi sciezke INSERT encji + UPDATE nodes z primary_entity_id
// + required_entity_ids bez React state, bez toast, bez hook props.
//
// PARITY: lowercase + trim canonical_name (zgodne z `uq_entity_per_map`
// expression-index + STOP-A `uq_entity_per_map_plain`), onConflict ignoreDuplicates.
// findEntity logic (exact / partial / alias) identyczna z UI handlerem.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import type {
  ThematicEntity,
  LLMEntityOutput,
  EntityType,
  ThematicNode,
} from "@/types/thematicMaps";
import { logDebug, logError } from "@/services/contentEngine/contentLogger";

export interface InsertEntitiesOutcome {
  /** Wszystkie encje po UPSERT (te ktore wlasnie wstawione + matched duplicates). */
  inserted: ThematicEntity[];
  /** Ile rows w batchu zostalo pominietych jako duplikaty (lowercase canonical_name). */
  duplicatesSkipped: number;
  /** Pierwsza tresc bledu (do bubble do UI / log). */
  firstError?: string;
}

export interface LinkEntitiesOutcome {
  /** Ile node'ow dostalo UPDATE primary_entity_id / required_entity_ids. */
  linked: number;
  /** Lista raw entity name'ow ktore NIE znalazly matcha — do UI feedback. */
  unmatched: string[];
  /** Liczba bledow UPDATE (DB / RLS). */
  failed: number;
  /** Pierwsza tresc bledu. */
  firstError?: string;
}

/**
 * UPSERT encji do `thematic_entities` z onConflict (map_id, canonical_name).
 *
 * Lowercase + trim canonical_name dla spojnosci z `uq_entity_per_map` (expression
 * lower()) + `uq_entity_per_map_plain` (plain z STOP-A migracji).
 *
 * Zwraca wszystkie encje (nowo dodane + duplikaty pominiete) — bulk runner musi
 * znac IDs zeby zrobic linking nodes -> entities. NOTE: Supabase upsert z
 * `ignoreDuplicates: true` NIE zwraca duplikatow w response, wiec musimy je
 * dociagnac drugim selectem.
 */
export async function insertEntitiesBulkRaw(
  userId: string,
  mapId: string,
  entities: LLMEntityOutput[],
): Promise<InsertEntitiesOutcome> {
  if (entities.length === 0) {
    return { inserted: [], duplicatesSkipped: 0 };
  }

  const rows = entities.map((e) => ({
    user_id: userId,
    map_id: mapId,
    canonical_name: (e.canonical_name ?? "").toLowerCase().trim(),
    entity_type: e.entity_type as EntityType,
    aliases: e.aliases || [],
    description: e.description || "",
  }));

  try {
    const { data, error } = await (supabase as any)
      .from("thematic_entities")
      .upsert(rows, {
        onConflict: "map_id,canonical_name",
        ignoreDuplicates: true,
      })
      .select();

    if (error) {
      logError("ENTITIES_INSERT", `Mapa ${mapId} — UPSERT error: ${error.message}`);
      return {
        inserted: [],
        duplicatesSkipped: 0,
        firstError: error.message,
      };
    }

    const justInserted = (data as ThematicEntity[]) || [];

    // Dociagnij pelna liste encji dla mapy (nowo wstawione + istniejace dupes)
    // — bulk runner musi znac IDs zeby zrobic linking.
    const { data: allForMap, error: fetchErr } = await (supabase as any)
      .from("thematic_entities")
      .select("*")
      .eq("map_id", mapId);

    if (fetchErr) {
      logError(
        "ENTITIES_INSERT",
        `Mapa ${mapId} — fetch po UPSERT failed: ${fetchErr.message}`,
      );
      return {
        inserted: justInserted,
        duplicatesSkipped: rows.length - justInserted.length,
        firstError: fetchErr.message,
      };
    }

    const allEntities = (allForMap as ThematicEntity[]) || [];
    const duplicatesSkipped = rows.length - justInserted.length;

    logDebug(
      "ENTITIES_INSERT",
      `Mapa ${mapId} — UPSERT OK: ${justInserted.length} new, ${duplicatesSkipped} duplikatow pominieto. Total dla mapy: ${allEntities.length}`,
    );

    return {
      inserted: allEntities,
      duplicatesSkipped,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "unknown";
    logError("ENTITIES_INSERT", `Mapa ${mapId} — UPSERT EXCEPTION: ${msg}`);
    return { inserted: [], duplicatesSkipped: 0, firstError: msg };
  }
}

/**
 * Linkuje encje do nodes — UPDATE per node z primary_entity_id (FK do thematic_entities)
 * + required_entity_ids (array UUID).
 *
 * Match strategy (identyczna z UI handlerem):
 *  1. Exact match po `canonical_name.toLowerCase().trim()`
 *  2. Exact match po `aliases[].toLowerCase()`
 *  3. Partial: rawName.includes(entityName) lub entityName.includes(rawName)
 *
 * NIE robi UPDATE dla nodes ktore juz maja `primary_entity_id` lub
 * `required_entity_ids.length > 0` (nie nadpisuje istniejacych linkings).
 */
export async function linkEntitiesToNodes(
  mapId: string,
  entities: ThematicEntity[],
  nodes: ThematicNode[],
): Promise<LinkEntitiesOutcome> {
  const outcome: LinkEntitiesOutcome = {
    linked: 0,
    unmatched: [],
    failed: 0,
  };

  if (entities.length === 0 || nodes.length === 0) {
    return outcome;
  }

  // Build lookup map: lowercase canonical_name + lowercase aliases -> entity
  const entityLookup = new Map<string, ThematicEntity>();
  for (const entity of entities) {
    entityLookup.set(entity.canonical_name.toLowerCase(), entity);
    for (const alias of entity.aliases || []) {
      if (!entityLookup.has(alias.toLowerCase())) {
        entityLookup.set(alias.toLowerCase(), entity);
      }
    }
  }

  const findEntity = (rawName: string): ThematicEntity | undefined => {
    const key = rawName.toLowerCase().trim();
    const exact = entityLookup.get(key);
    if (exact) return exact;

    // Partial: includes-match w obie strony
    for (const entity of entities) {
      const eName = entity.canonical_name.toLowerCase();
      if (eName.includes(key) || key.includes(eName)) return entity;
      for (const alias of entity.aliases || []) {
        const aName = alias.toLowerCase();
        if (aName.includes(key) || key.includes(aName)) return entity;
      }
    }
    return undefined;
  };

  for (const node of nodes) {
    const updates: Record<string, unknown> = {};
    let changed = false;

    if (node.primary_entity_raw && !node.primary_entity_id) {
      const match = findEntity(node.primary_entity_raw);
      if (match) {
        updates.primary_entity_id = match.id;
        changed = true;
      } else {
        outcome.unmatched.push(node.primary_entity_raw);
      }
    }

    const reqRaws = node.required_entities_raw || [];
    const reqIds = node.required_entity_ids || [];
    if (reqRaws.length > 0 && reqIds.length === 0) {
      const matchedIds: string[] = [];
      for (const rawName of reqRaws) {
        const match = findEntity(rawName);
        if (match) matchedIds.push(match.id);
      }
      if (matchedIds.length > 0) {
        updates.required_entity_ids = matchedIds;
        changed = true;
      }
    }

    if (changed) {
      try {
        const { error } = await (supabase as any)
          .from("thematic_nodes")
          .update(updates)
          .eq("id", node.id);

        if (error) {
          outcome.failed++;
          if (!outcome.firstError) outcome.firstError = error.message;
          logError(
            "ENTITIES_LINK",
            `Mapa ${mapId} — node "${node.title}" UPDATE error: ${error.message}`,
          );
        } else {
          outcome.linked++;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "unknown";
        outcome.failed++;
        if (!outcome.firstError) outcome.firstError = msg;
        logError(
          "ENTITIES_LINK",
          `Mapa ${mapId} — node "${node.title}" UPDATE EXCEPTION: ${msg}`,
        );
      }
    }
  }

  logDebug(
    "ENTITIES_LINK",
    `Mapa ${mapId} — LINK DONE: ${outcome.linked} linked, ${outcome.unmatched.length} unmatched, ${outcome.failed} failed`,
  );

  return outcome;
}

/**
 * 2-fazowy wrapper: UPSERT encji + linkowanie nodes.
 *
 * Bulk runner (STOP-D) wywoluje to bezposrednio po `extractEntitiesAI`.
 */
export async function insertEntitiesAndLink(args: {
  userId: string;
  mapId: string;
  entities: LLMEntityOutput[];
  nodes: ThematicNode[];
}): Promise<{
  insertOutcome: InsertEntitiesOutcome;
  linkOutcome: LinkEntitiesOutcome;
}> {
  const insertOutcome = await insertEntitiesBulkRaw(
    args.userId,
    args.mapId,
    args.entities,
  );

  const linkOutcome = await linkEntitiesToNodes(
    args.mapId,
    insertOutcome.inserted,
    args.nodes,
  );

  return { insertOutcome, linkOutcome };
}
